import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Eye, EyeOff, Search } from "lucide-react";
import { useNavigate } from "react-router";

import { useMe } from "@/api/me";
import { useViewAsSearch } from "@/api/users";
import { NAV_GROUPS, filterNav, flattenNav, personaLabel } from "@/components/layout/nav";
import { useStartViewAs, useStopViewAs } from "@/lib/impersonation";
import {
  Kbd,
  ShortcutsDialog,
  shellOverlays,
  useShellOverlay,
} from "@/lib/shortcuts";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/authStore";

/**
 * ⌘K — "Search or jump" (MV2-845, app/Main top bar; ops/Shortcuts).
 *
 * Jumps to any destination the persona owns. Site search lands with the
 * Sites page ticket; until then the palette is the keyboard's route map.
 *
 * MV2-1254 — for a SuperAdmin it is also the quickest way into and out of
 * View as: typing a name (2+ letters) offers "View as <name>" for any user
 * at any client, and while viewing as, "Stop viewing as" leads the list.
 */
interface Entry {
  id: string;
  label: string;
  sub?: string;
  icon: React.ComponentType<{ size?: number }>;
  hint?: string;
  run: () => void;
}

export function CommandPalette(): React.ReactElement {
  const open = useShellOverlay() === "palette";
  const navigate = useNavigate();
  const { data: me } = useMe();
  const isImpersonating = useAuthStore((s) => s.actingAs?.clientId != null);
  const isSuperAdmin = useAuthStore((s) => s.groups?.includes("SuperAdmin") ?? false);
  const viewingAs = useAuthStore((s) => s.viewingAs ?? null);
  const startViewAs = useStartViewAs();
  const stopViewAs = useStopViewAs();
  const [q, setQ] = React.useState("");
  const [cursor, setCursor] = React.useState(0);
  // A beat after the last keystroke, not one request per letter.
  const [userQ, setUserQ] = React.useState("");
  React.useEffect(() => {
    const t = window.setTimeout(() => setUserQ(q), 200);
    return () => window.clearTimeout(t);
  }, [q]);
  const people = useViewAsSearch(userQ, open && isSuperAdmin);

  const items = React.useMemo(
    () =>
      flattenNav(
        filterNav(
          NAV_GROUPS,
          me?.primary_persona ?? null,
          isImpersonating,
          me?.features ?? null,
        ),
      ),
    [me?.primary_persona, me?.features, isImpersonating],
  );
  const needle = q.trim().toLowerCase();
  const pages = needle
    ? items.filter(
        (i) => i.label.toLowerCase().includes(needle) || i.to.includes(needle),
      )
    : items;
  const stop: Entry[] =
    viewingAs && (!needle || `stop viewing as ${viewingAs.name}`.toLowerCase().includes(needle))
      ? [
          {
            id: "stop-view-as",
            label: `Stop viewing as ${viewingAs.name}`,
            sub: "back to yourself, where you were",
            icon: EyeOff,
            run: stopViewAs,
          },
        ]
      : [];
  const viewAs: Entry[] =
    isSuperAdmin && needle.length >= 2
      ? (people.data ?? [])
          .filter((u) => u.id !== viewingAs?.userId)
          .map((u) => {
            const persona =
              u.persona_class ?? (u.cognito_group === "ClientAdmin" ? "ClientAdmin" : null);
            return {
              id: `view-as-${u.id}`,
              label: `View as ${u.full_name}`,
              sub: [personaLabel(persona), u.client_name].filter(Boolean).join(" · "),
              icon: Eye,
              run: () =>
                startViewAs({
                  userId: u.id,
                  name: u.full_name,
                  persona,
                  clientId: u.client_id,
                  clientName: u.client_name ?? "",
                }),
            };
          })
      : [];
  const results: Entry[] = [
    ...stop,
    ...pages.map((i) => ({
      id: i.to,
      label: i.label,
      icon: i.icon,
      hint: i.key ? `g ${i.key}` : undefined,
      run: () => navigate(i.to),
    })),
    ...viewAs,
  ];

  React.useEffect(() => {
    if (!open) {
      setQ("");
      setCursor(0);
    }
  }, [open]);
  React.useEffect(() => setCursor(0), [needle]);

  const go = (e: Entry) => {
    shellOverlays.close();
    e.run();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(results.length - 1, c + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === "Enter") {
      const hit = results[cursor];
      if (hit) {
        e.preventDefault();
        go(hit);
      }
    }
  };

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(o) => !o && shellOverlays.close()}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-[color:var(--gray-900)]/40 data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <DialogPrimitive.Content
          aria-label="Search or jump"
          className="fixed left-1/2 top-[15vh] z-50 w-[min(560px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-[12px] border border-gray-200 bg-surface shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=open]:zoom-in-95 motion-reduce:animate-none"
        >
          <DialogPrimitive.Title className="sr-only">
            Search or jump
          </DialogPrimitive.Title>
          <div className="flex h-11 items-center gap-2.5 border-b border-gray-200 px-3.5">
            <Search size={15} className="flex-none text-gray-500" aria-hidden />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search or jump…"
              aria-label="Search or jump"
              role="combobox"
              aria-expanded
              aria-controls="command-palette-results"
              aria-activedescendant={
                results[cursor] ? `cp-${results[cursor].id}` : undefined
              }
              className="h-full flex-1 bg-transparent text-[13px] text-gray-900 outline-none placeholder:text-gray-500"
            />
            <Kbd>esc</Kbd>
          </div>
          <ul
            id="command-palette-results"
            role="listbox"
            className="max-h-[50vh] overflow-y-auto p-1.5"
          >
            {results.length === 0 && (
              <li className="px-3 py-3 text-[12.5px] text-gray-500">
                Nothing matches “{q}”.
              </li>
            )}
            {results.map((i, idx) => {
              const Icon = i.icon;
              const on = idx === cursor;
              return (
                <li
                  key={i.id}
                  id={`cp-${i.id}`}
                  role="option"
                  aria-selected={on}
                  onMouseEnter={() => setCursor(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => go(i)}
                  className={cn(
                    "flex h-9 cursor-pointer items-center gap-2.5 rounded-[7px] px-2.5 text-[13px]",
                    on ? "bg-gray-100 text-gray-900" : "text-gray-700",
                  )}
                >
                  <Icon size={15} />
                  <span className="min-w-0 flex-1 truncate">{i.label}</span>
                  {i.sub && <span className="type-meta truncate">{i.sub}</span>}
                  {i.hint && <Kbd>{i.hint}</Kbd>}
                </li>
              );
            })}
          </ul>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Mounts the key handler and the two overlays. Once, inside the router. */
export function GlobalShortcuts(): React.ReactElement {
  return (
    <>
      <CommandPalette />
      <ShortcutsDialog />
    </>
  );
}
